import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, KeyRound, Loader2, MailCheck, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { CosmicBackground } from "@/components/cosmic-background";
import { Button } from "@/components/ui/button";
import { PasswordField } from "@/components/auth/password-field";
import { ForgotPasswordDialog } from "@/components/auth/forgot-password-dialog";
import {
  applyFirebaseActionCode,
  confirmPasswordReset,
  firebaseActionErrorMessage,
  parseFirebaseActionMode,
  verifyPasswordResetCode,
  type FirebaseActionMode,
} from "@/lib/firebase";
import { markHasPassword, validateNewPassword } from "@/lib/password";
import { errorName, trackEvent } from "@/lib/analytics";

/**
 * Trang đích của link trong email Firebase — ĐẶT LẠI MẬT KHẨU và XÁC THỰC EMAIL.
 *
 * 🔴 MỘT TRANG, NHIỀU CHẾ ĐỘ: Firebase chỉ cho khai MỘT "Action URL" cho mỗi mẫu
 * email, và mọi mẫu (đặt lại mật khẩu, xác thực email, khôi phục email) đều gắn thêm
 * `?mode=` vào cùng một URL. Trỏ Action URL về đây mà trang chỉ xử lý `resetPassword`
 * thì người vừa đăng ký bấm link xác thực sẽ thấy "link không hợp lệ" trong khi tài
 * khoản của họ vẫn nằm im chưa kích hoạt — hỏng ở đúng bước đầu tiên của vòng đời
 * người dùng.
 *
 * 🔴 KHÔNG GỌI BE Ở ĐÂY. `oobCode` do Firebase phát hành và chỉ Firebase xác nhận
 * được; BE không có endpoint nào nhận nó (hàm `confirm_password_reset` bên BE viết
 * rồi nhưng chưa nối vào view nào).
 *
 * ⚠️ Trang này chỉ hoạt động sau khi đổi Action URL trong Firebase Console
 * (Authentication → Templates → từng mẫu email → "Customize action URL") thành
 * `https://boide.online/dat-lai-mat-khau`. Chưa đổi thì link vẫn về trang mặc định
 * của Firebase và trang này không bao giờ được mở — không có lỗi nào để lần ra.
 */

type Search = {
  mode?: string;
  oobCode?: string;
  /** Firebase còn gắn `apiKey`, `continueUrl`, `lang` — giữ lại để không mất khi điều hướng. */
  continueUrl?: string;
  lang?: string;
};

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

export const Route = createFileRoute("/dat-lai-mat-khau")({
  validateSearch: (search): Search => ({
    mode: asString(search.mode),
    oobCode: asString(search.oobCode),
    continueUrl: asString(search.continueUrl),
    lang: asString(search.lang),
  }),
  head: () => ({
    meta: [
      { title: "Đặt Lại Mật Khẩu - Bói Đê" },
      { name: "description", content: "Đặt lại mật khẩu tài khoản Bói Đê." },
      // Trang chỉ có nghĩa khi đi kèm oobCode trong link mail — không có gì để index.
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ResetPasswordPage,
});

type Phase =
  | { kind: "checking" }
  | { kind: "form"; email: string }
  | { kind: "done"; mode: FirebaseActionMode }
  | { kind: "error"; message: string };

function ResetPasswordPage() {
  const { mode: rawMode, oobCode } = Route.useSearch();
  // Thiếu `mode` thì coi như đặt lại mật khẩu: đó là lý do gần như duy nhất khiến
  // người dùng mở trang này bằng tay, và nếu đoán sai thì oobCode sẽ tự báo sai.
  const mode = rawMode ? parseFirebaseActionMode(rawMode) : "resetPassword";
  const [phase, setPhase] = useState<Phase>({ kind: "checking" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const start = useCallback(async () => {
    if (!oobCode) {
      setPhase({
        kind: "error",
        message:
          "Link không đầy đủ. Hãy mở lại đúng link trong email, hoặc bấm nút bên dưới để nhận link mới.",
      });
      return;
    }
    setPhase({ kind: "checking" });
    try {
      if (mode === "verifyEmail" || mode === "recoverEmail") {
        await applyFirebaseActionCode(oobCode);
        trackEvent(mode === "verifyEmail" ? "auth_verify_email_done" : "auth_recover_email_done");
        setPhase({ kind: "done", mode });
        return;
      }
      // Kiểm oobCode TRƯỚC khi hiện form: link hết hạn là chuyện rất thường (hiệu lực
      // ~1 giờ, và mỗi lần bấm "quên mật khẩu" lại vô hiệu hoá link cũ). Báo ngay còn
      // hơn để người dùng gõ xong mật khẩu mới rồi mới đổ.
      const email = await verifyPasswordResetCode(oobCode);
      setPhase({ kind: "form", email });
    } catch (err) {
      trackEvent("auth_action_code_invalid", { error_name: errorName(err) });
      setPhase({ kind: "error", message: firebaseActionErrorMessage(err) });
    }
  }, [mode, oobCode]);

  useEffect(() => {
    void start();
  }, [start]);

  const submit = async () => {
    if (phase.kind !== "form" || !oobCode) return;
    const error = validateNewPassword(password, confirm);
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      await confirmPasswordReset(oobCode, password);
      // Lần sau vào "đổi mật khẩu" thì màn đó phải hỏi mật khẩu hiện tại, kể cả với
      // tài khoản tạo bằng Google — xem `knownHasPassword`.
      markHasPassword(phase.email);
      trackEvent("auth_reset_password_done");
      setPhase({ kind: "done", mode: "resetPassword" });
    } catch (err) {
      trackEvent("auth_reset_password_failed", { error_name: errorName(err) });
      toast.error(firebaseActionErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CosmicBackground />
      <div className="flex-1 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center px-4 py-8 sm:py-12">
          <div className="w-full max-w-md rounded-3xl border border-gold-soft bg-card/70 p-6 shadow-mystic backdrop-blur-xl sm:p-10">
            <h1 className="flex items-center justify-center gap-2 text-center font-serif-soft text-2xl sm:text-3xl">
              <KeyRound className="size-5 text-primary" />
              {mode === "verifyEmail" ? "Xác thực email" : "Đặt lại mật khẩu"}
            </h1>

            {phase.kind === "checking" ? (
              <div className="mt-8 flex flex-col items-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="size-6 animate-spin text-primary" />
                Đang kiểm tra link...
              </div>
            ) : null}

            {phase.kind === "form" ? (
              <div className="mt-6 space-y-4">
                <p className="text-center text-sm text-muted-foreground">
                  Đặt mật khẩu mới cho <strong className="text-foreground">{phase.email}</strong>
                </p>
                <PasswordField
                  label="Mật khẩu mới"
                  value={password}
                  onChange={setPassword}
                  autoComplete="new-password"
                  disabled={saving}
                  showStrength
                  autoFocus
                  hint="Ít nhất 8 ký tự, có cả chữ và số, không dấu tiếng Việt."
                />
                <PasswordField
                  label="Nhập lại mật khẩu mới"
                  value={confirm}
                  onChange={setConfirm}
                  autoComplete="new-password"
                  disabled={saving}
                />
                <Button className="w-full" disabled={saving} onClick={() => void submit()}>
                  {saving ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                  {saving ? "Đang đặt lại..." : "Đặt lại mật khẩu"}
                </Button>
              </div>
            ) : null}

            {phase.kind === "done" ? (
              <div className="mt-6 space-y-4 text-center">
                <div className="flex justify-center">
                  {phase.mode === "resetPassword" ? (
                    <CheckCircle2 className="size-10 text-emerald-500" />
                  ) : (
                    <MailCheck className="size-10 text-emerald-500" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {phase.mode === "resetPassword"
                    ? "Đã đổi mật khẩu thành công. Bạn có thể đăng nhập bằng mật khẩu mới ngay bây giờ."
                    : "Email đã được xác thực. Bạn có thể đăng nhập và bắt đầu sử dụng Bói Đê."}
                </p>
                <Button asChild className="w-full">
                  <Link to="/login">Đăng nhập</Link>
                </Button>
              </div>
            ) : null}

            {phase.kind === "error" ? (
              <div className="mt-6 space-y-4">
                <div className="flex gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
                  <TriangleAlert className="mt-0.5 size-5 shrink-0 text-destructive" />
                  <span className="text-muted-foreground">{phase.message}</span>
                </div>
                <Button className="w-full" onClick={() => setForgotOpen(true)}>
                  Nhận link mới
                </Button>
                <Button asChild variant="ghost" className="w-full">
                  <Link to="/login">Về trang đăng nhập</Link>
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </>
  );
}
